import React, { useRef, useState, useEffect } from 'react'; 
import './DrawingBoard.css';
import { Icon } from '@iconify/react';

const DrawingBoard = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [penSize, setPenSize] = useState(5);
  const [penColor, setPenColor] = useState('black');
  const [drawingShape, setDrawingShape] = useState('freehand');
  const [isEraser, setIsEraser] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [showPenSize, setShowPenSize] = useState(false);
  const [showPenColor, setShowPenColor] = useState(false);
  const [showShapeOptions, setShowShapeOptions] = useState(false);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const context = canvas.getContext('2d');
    setCtx(context);
    context.lineCap = 'round';
  }, []);

  useEffect(() => {
    if (ctx) {
      ctx.strokeStyle = isEraser ? 'white' : penColor;
      ctx.lineWidth = penSize;
    }
  }, [penColor, penSize, isEraser, ctx]);

  const saveHistory = () => {
    const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHistory((prev) => [...prev, imageData]);
    setRedoStack([]);
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    setStartPos({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
    saveHistory();
    if (drawingShape === 'freehand') {
      ctx.beginPath();
      ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;
    if (drawingShape === 'freehand') {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const x = e.nativeEvent.offsetX;
    const y = e.nativeEvent.offsetY;

    if (drawingShape === 'rectangle') {
      ctx.strokeRect(startPos.x, startPos.y, x - startPos.x, y - startPos.y);
    } else if (drawingShape === 'circle') {
      const radius = Math.sqrt(
        Math.pow(x - startPos.x, 2) + Math.pow(y - startPos.y, 2)
      );
      ctx.beginPath();
      ctx.arc(startPos.x, startPos.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  };

  const clearBoard = () => {
    saveHistory();
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const saveImage = () => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasRef.current.width;
    tempCanvas.height = canvasRef.current.height;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.fillStyle = 'white';
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(canvasRef.current, 0, 0);
    const link = document.createElement('a');
    link.download = 'drawing.png';
    link.href = tempCanvas.toDataURL();
    link.click();
  };

  const toggleEraser = () => {
    setIsEraser((prev) => !prev);
  };

  const undo = () => {
    if (history.length > 0) {
      const previousState = history[history.length - 1];
      setRedoStack((prev) => [...prev, ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height)]);
      ctx.putImageData(previousState, 0, 0);
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  const redo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      setHistory((prev) => [...prev, ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height)]);
      ctx.putImageData(nextState, 0, 0);
      setRedoStack((prev) => prev.slice(0, -1));
    }
  };

  const btnStyle = {
    margin: '5px',
    padding: '4px 8px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '5px',
    backgroundColor: '#4f4f4f',
    color: 'white',
    cursor: 'pointer'
  };

  const iconBtnStyle = {
    margin: '5px',
    padding: '4px 8px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '5px',
    backgroundColor: '#ffffff',
    color: 'white',
    cursor: 'pointer'
  };

  const correctIconStyle = {
    color: '#32cd32',
    marginRight: '5px',
    verticalAlign: 'middle',
  };

  const incorrectIconStyle = {
    color: '#ff0000',
    marginRight: '5px',
    verticalAlign: 'middle',
  };

  const redoIconStyle = {
    color: '#000000',
    marginRight: '0px',
    verticalAlign: 'middle',
  };

  const undoIconStyle = {
    color: '#000000',
    marginRight: '0px',
    verticalAlign: 'middle',
  };

  return (
    <div className="drawing-container">
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
      />
      <div className="controls">
      <button onClick={() => setShowPenSize(!showPenSize)} style={btnStyle}>Size</button>
        <button onClick={() => setShowPenColor(!showPenColor)} style={btnStyle} >Colour</button>
        <button onClick={() => setShowShapeOptions(!showShapeOptions)} style={btnStyle} >Shape</button>
        <button onClick={toggleEraser} style={iconBtnStyle} >{isEraser ? <Icon icon="fluent-emoji:pen" width="20" height="20" style={undoIconStyle}/> : <Icon icon="jam:rubber" width="20" height="20" style={undoIconStyle}/>}</button>
        <button onClick={undo} style={iconBtnStyle}><Icon icon="material-symbols:undo" width="20" height="20" style={undoIconStyle}/></button>
        <button onClick={redo} style={iconBtnStyle}><Icon icon="material-symbols:redo" width="20" height="20" style={redoIconStyle}/></button>
        <button onClick={clearBoard} style={iconBtnStyle}><Icon icon="icomoon-free:cross" width="20" height="20" style={incorrectIconStyle} /></button>
        <button onClick={saveImage} style={iconBtnStyle}><Icon icon="icon-park-solid:correct" width="20" height="20" style={correctIconStyle} /></button>
      </div>

      <div className="control-popups">
        {showPenSize && (
          <label>
            Pen Size:
            <input
              type="range"
              min="1"
              max="20"
              value={penSize}
              onChange={(e) => setPenSize(Number(e.target.value))}
            />
          </label>
        )}

        {showPenColor && (
          <label>
            Pen Color:
            <input
              type="color"
              value={penColor}
              onChange={(e) => {
                setPenColor(e.target.value);
                setIsEraser(false);
              }}
            />
          </label>
        )}

        {showShapeOptions && (
          <label>
            Shape:
            <select
              value={drawingShape}
              onChange={(e) => setDrawingShape(e.target.value)}
            >
              <option value="freehand">Freehand</option>
              <option value="rectangle">Rectangle</option>
              <option value="circle">Circle</option>
            </select>
          </label>
        )}
      </div>
    </div>
  );
};

export default DrawingBoard;
